// src/pages/finance/PaymentRequestsPage.tsx
import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, FileText, Clock, CheckCircle, XCircle, DollarSign, Wallet, Trash2, CalendarIcon, X, Files } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { usePaymentRequests } from '@/hooks/finance/usePaymentRequests';
import {
  useBulkPaymentRequests,
  type BulkPaymentRequestGroup,
} from '@/hooks/finance/usePaymentRequestsBulk';
import { useDeletePaymentRequest } from '@/hooks/finance/useDeletePaymentRequest';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { PaymentRequestStatus } from '@/types/database/payment-requests';
import { format, parseISO, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/currency';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/finance/DeleteConfirmationDialog';
import { useFinancePagination } from '@/hooks/use-finance-pagination';
import FinancePagination from '@/components/finance/FinancePagination';
import { cn } from '@/lib/utils';

const statusConfig: Record<PaymentRequestStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <FileText className="h-3 w-3" /> },
  pending_approval: { label: 'Pending Approval', variant: 'outline', icon: <Clock className="h-3 w-3" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  paid: { label: 'Paid', variant: 'default', icon: <DollarSign className="h-3 w-3" /> },
  cancelled: { label: 'Cancelled', variant: 'secondary', icon: <XCircle className="h-3 w-3" /> },
};

const parsePaymentReference = (ref: string | null) => {
  if (!ref) return { bankAccount: null, reference: null };
  const parts = ref.split(' | ');
  if (parts.length >= 2) {
    return { bankAccount: parts[0], reference: parts.slice(1).join(' | ') };
  }
  return { bankAccount: null, reference: ref };
};

const PaymentRequestsPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [requestToDelete, setRequestToDelete] = React.useState<{ id: string; number: string } | null>(null);

  // URL-persisted state
  const activeTab = searchParams.get('tab') || 'all';
  const searchTerm = searchParams.get('search') || '';
  const selectedDepartment = searchParams.get('dept') || 'all';
  const selectedCategory = searchParams.get('cat') || 'all';
  const dateFromStr = searchParams.get('from') || '';
  const dateToStr = searchParams.get('to') || '';

  const dateFrom = dateFromStr ? new Date(dateFromStr) : undefined;
  const dateTo = dateToStr ? new Date(dateToStr) : undefined;

  const updateParam = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all' || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      // Reset page when filters change
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const isAdmin = userRole === 'admin';

  const statusFilter = activeTab === 'all' ? undefined : activeTab as PaymentRequestStatus;

  const {
    data: requests = [],
    isLoading: isSingleLoading,
  } = usePaymentRequests(statusFilter);

  const {
    data: bulkRequests = [],
    isLoading: isBulkLoading,
  } = useBulkPaymentRequests(statusFilter);

  const isLoading = isSingleLoading || isBulkLoading;

  const { data: departments } = useDepartments();
  const deletePaymentRequest = useDeletePaymentRequest();

  const singleRequests = useMemo(() => {
    return requests.filter((req) => !(req.upload_method === 'bulk_csv' && req.upload_id));
  }, [requests]);

  const categories = useMemo(() => {
    const catMap = new Map<string, string>();

    singleRequests.forEach((req) => {
      if (req.category?.id && req.category?.name) {
        catMap.set(req.category.id, req.category.name);
      }
    });

    bulkRequests.forEach((group) => {
      group.categories.forEach((cat) => {
        catMap.set(cat.id, cat.name);
      });
    });

    return Array.from(catMap, ([id, name]) => ({ id, name }));
  }, [singleRequests, bulkRequests]);

  const handleDeleteClick = (e: React.MouseEvent, id: string, requestNumber: string) => {
    e.stopPropagation();
    setRequestToDelete({ id, number: requestNumber });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!requestToDelete) return;
    await deletePaymentRequest.mutateAsync(requestToDelete.id);
    setDeleteDialogOpen(false);
    setRequestToDelete(null);
  };

  type SingleRequestRow = (typeof singleRequests)[number];

  type DisplayItem =
    | { type: 'single'; request: SingleRequestRow; sortDate: string }
    | { type: 'bulk'; group: BulkPaymentRequestGroup; sortDate: string };

  const displayItems = useMemo<DisplayItem[]>(() => {
    const term = searchTerm.trim().toLowerCase();

    const filteredSingleItems: DisplayItem[] = singleRequests
      .filter((req) => {
        const matchesSearch =
          !term ||
          req.title.toLowerCase().includes(term) ||
          req.request_number.toLowerCase().includes(term) ||
          req.vendor_name?.toLowerCase().includes(term) ||
          req.amount?.toString().includes(term) ||
          req.total_amount?.toString().includes(term);

        const matchesDepartment =
          selectedDepartment === 'all' || req.department_id === selectedDepartment;

        const matchesCategory =
          selectedCategory === 'all' || req.category_id === selectedCategory;

        let matchesDateFrom = true;
        let matchesDateTo = true;

        if (dateFrom) {
          matchesDateFrom = !isBefore(parseISO(req.created_at), startOfDay(dateFrom));
        }

        if (dateTo) {
          matchesDateTo = !isAfter(parseISO(req.created_at), endOfDay(dateTo));
        }

        return matchesSearch && matchesDepartment && matchesCategory && matchesDateFrom && matchesDateTo;
      })
      .map((request) => ({
        type: 'single' as const,
        request,
        sortDate: request.created_at,
      }));

    const filteredBulkItems: DisplayItem[] = bulkRequests
      .filter((group) => {
        const matchesSearch = !term || group.searchIndex.includes(term);

        const matchesDepartment =
          selectedDepartment === 'all' || group.departmentId === selectedDepartment;

        const matchesCategory =
          selectedCategory === 'all' || group.categoryIds.includes(selectedCategory);

        let matchesDateFrom = true;
        let matchesDateTo = true;

        if (dateFrom) {
          matchesDateFrom = !isBefore(parseISO(group.createdAt), startOfDay(dateFrom));
        }

        if (dateTo) {
          matchesDateTo = !isAfter(parseISO(group.createdAt), endOfDay(dateTo));
        }

        return matchesSearch && matchesDepartment && matchesCategory && matchesDateFrom && matchesDateTo;
      })
      .map((group) => ({
        type: 'bulk' as const,
        group,
        sortDate: group.createdAt,
      }));

    return [...filteredBulkItems, ...filteredSingleItems].sort(
      (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
    );
  }, [
    singleRequests,
    bulkRequests,
    searchTerm,
    selectedDepartment,
    selectedCategory,
    dateFrom,
    dateTo,
  ]);

  const pagination = useFinancePagination({ data: displayItems, itemsPerPage: 10 });


  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Payment Requests</h1>
            <p className="text-muted-foreground">Create and manage payment requests</p>
          </div>
          <Button onClick={() => navigate('/finance/accounting/payment-requests/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, number, vendor, or amount..."
                value={searchTerm}
                onChange={(e) => updateParam('search', e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedDepartment} onValueChange={(v) => updateParam('dept', v)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedCategory} onValueChange={(v) => updateParam('cat', v)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date From */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[180px] justify-start text-left font-normal",
                    !dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={(d) => updateParam('from', d ? format(d, 'yyyy-MM-dd') : '')}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            {/* Date To */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[180px] justify-start text-left font-normal",
                    !dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={(d) => updateParam('to', d ? format(d, 'yyyy-MM-dd') : '')}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            {/* Clear filters */}
            {(searchTerm || selectedDepartment !== 'all' || selectedCategory !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams(prev => {
                  const next = new URLSearchParams();
                  const tab = prev.get('tab');
                  if (tab) next.set('tab', tab);
                  return next;
                })}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => updateParam('tab', v)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Drafts</TabsTrigger>
            <TabsTrigger value="pending_approval">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-1/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : displayItems.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No payment requests found</h3>
                  <p className="text-muted-foreground mb-4">
                    {activeTab === 'all'
                      ? "Create your first payment request to get started"
                      : `No ${statusConfig[activeTab as PaymentRequestStatus]?.label.toLowerCase()} requests`}
                  </p>
                  <Button onClick={() => navigate('/finance/accounting/payment-requests/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Request
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pagination.paginatedData.map((item) => {
                  if (item.type === 'bulk') {
                    const { group } = item;

                    return (
                      <Card
                        key={`bulk-${group.uploadId}`}
                        className="cursor-pointer hover:bg-muted/50 transition-colors border-dashed"
                        onClick={() => navigate(`/finance/accounting/payment-requests/bulk/${group.uploadId}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="gap-1">
                                  <Files className="h-3 w-3" />
                                  <span>Bulk request</span>
                                </Badge>

                                {group.bulkStatus !== 'mixed' ? (
                                  <Badge variant={statusConfig[group.bulkStatus].variant}>
                                    {statusConfig[group.bulkStatus].icon}
                                    <span className="ml-1">{statusConfig[group.bulkStatus].label}</span>
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    <Clock className="h-3 w-3" />
                                    <span className="ml-1">Mixed statuses</span>
                                  </Badge>
                                )}
                              </div>

                              <h3 className="font-medium">Uploaded from CSV</h3>

                              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                                <span>{group.requestsCount} payment requests</span>
                                <span>• {format(new Date(group.createdAt), 'MMM d, yyyy')}</span>

                                {group.department && (
                                  <span>• {group.department.name}</span>
                                )}

                                {group.categories.length > 0 && (
                                  <span>• {group.categories.map((cat) => cat.name).join(', ')}</span>
                                )}
                              </div>
                            </div>

                            <div className="text-right">
                              <p className="text-lg font-semibold">
                                {formatCurrency(group.totalAmount, group.currency || 'ETB')}
                              </p>
                              <p className="text-xs text-muted-foreground">Total amount</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }

                  const request = item.request;

                  return (
                    <Card
                      key={request.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/finance/accounting/payment-requests/${request.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-muted-foreground">
                                {request.request_number}
                              </span>
                              <Badge variant={statusConfig[request.status].variant}>
                                {statusConfig[request.status].icon}
                                <span className="ml-1">{statusConfig[request.status].label}</span>
                              </Badge>
                            </div>

                            <h3 className="font-medium">{request.title}</h3>

                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {request.department && (
                                <span>{request.department.name}</span>
                              )}
                              {request.vendor_name && (
                                <span>• {request.vendor_name}</span>
                              )}
                              <span>• {format(new Date(request.created_at), 'MMM d, yyyy')}</span>
                            </div>

                            {request.status === 'paid' && request.payment_reference && (() => {
                              const { bankAccount } = parsePaymentReference(request.payment_reference);
                              return bankAccount ? (
                                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                                  <Wallet className="h-3.5 w-3.5" />
                                  <span>Paid from: {bankAccount}</span>
                                </div>
                              ) : null;
                            })()}
                          </div>

                          <div className="text-right flex items-start gap-2">
                            <div>
                              {request.status === 'paid' && request.expense?.[0]?.net_payment ? (
                                <>
                                  <p className="text-lg font-semibold">
                                    {formatCurrency(request.expense[0].net_payment, request.currency || 'ETB')}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Net Paid</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-lg font-semibold">
                                    {formatCurrency(request.total_amount, request.currency || 'ETB')}
                                  </p>
                                  {request.preferred_payment_date && (
                                    <p className="text-sm text-muted-foreground">
                                      Due: {format(new Date(request.preferred_payment_date), 'MMM d')}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>

                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={(e) => handleDeleteClick(e, request.id, request.request_number)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                <FinancePagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.goToPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPreviousPage={pagination.hasPreviousPage}
                  totalItems={pagination.totalItems}
                  startIndex={pagination.startIndex}
                  endIndex={pagination.endIndex}
                  itemsPerPage={pagination.itemsPerPage}
                  onItemsPerPageChange={pagination.changePageSize}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          isDeleting={deletePaymentRequest.isPending}
          title="Delete Payment Request"
          description={`Are you sure you want to delete ${requestToDelete?.number}? This action cannot be undone.`}
        />
      </div>
    </Layout>
  );
};

export default PaymentRequestsPage;
